import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui/card";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/select";
import { Textarea } from "@repo/ui/textarea";
import {
  Copy,
  ExternalLink,
  Loader2,
  PlayCircle,
  Search as SearchIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { invokeAdapterVisibility } from "@/lib/api";
import type { AdapterVisibilityResponse } from "@/lib/api";

type PreviewMode = "detail" | "discovery";

type PreviewAction = PreviewMode | "stored";

/** "auto" follows production routing; the rest force one provider for the preview. */
const PREVIEW_PROVIDERS = [
  { value: "auto", label: "Production routing" },
  { value: "direct", label: "Direct fetch only (free)" },
  { value: "firecrawl_scrape", label: "Firecrawl only" },
  { value: "zyte", label: "Zyte only" },
  { value: "brightdata", label: "Bright Data only" },
] as const;

type PreviewProvider = (typeof PREVIEW_PROVIDERS)[number]["value"];

interface DiscoverSamplePanelProps {
  adapterSlug: string;
  /** Pretty-printed JSON object of adapter config (merged with defaults). */
  configJson: string;
  onConfigJsonChange: (value: string) => void;
  /** Optional URL pre-populated from a cross-tab handoff (query string). */
  initialUrl?: string;
  /** Optional preview mode pre-populated from a cross-tab handoff. */
  initialMode?: PreviewMode | null;
  /** Optional list of listing URLs to make the "pick a listing URL" picker easy. */
  listingUrls?: Array<{ page: number; url: string }>;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function display(value: unknown): string {
  return value === undefined ? "—" : JSON.stringify(value);
}

/**
 * Unified discover + sample preview panel. Replaces the old "Sample scrape"
 * tab. Supports detail preview (inline extracted JSON) and discovery
 * preview (clickable list of property URLs that re-invoke as detail).
 */
export function DiscoverSamplePanel({
  adapterSlug,
  configJson,
  onConfigJsonChange,
  initialUrl,
  initialMode,
  listingUrls = [],
}: DiscoverSamplePanelProps) {
  const { toast } = useToast();
  const { canManageJobs } = useAuth();
  const [previewUrl, setPreviewUrl] = useState(initialUrl ?? "");
  const [mode, setMode] = useState<PreviewMode>(initialMode ?? "detail");
  const [previewLoading, setPreviewLoading] = useState<PreviewAction | null>(null);
  const [previewResult, setPreviewResult] = useState<AdapterVisibilityResponse | null>(null);
  const [previewProvider, setPreviewProvider] = useState<PreviewProvider>("auto");
  // Track whether an auto-run from the URL handoff has already happened so
  // we don't fire it repeatedly when the user edits state afterwards.
  const autoRanRef = useRef(false);

  const parsedConfig = useMemo((): Record<string, unknown> | null => {
    try {
      const raw = JSON.parse(configJson || "{}");
      if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return {};
      return raw as Record<string, unknown>;
    } catch {
      return null;
    }
  }, [configJson]);

  const runPreview = async (action: PreviewAction, urlOverride?: string) => {
    if (!adapterSlug) return;
    if (parsedConfig === null) {
      toast({
        title: "Invalid config JSON",
        description: "Fix the JSON object in the config field.",
        variant: "destructive",
      });
      return;
    }
    const url = (urlOverride ?? previewUrl).trim();
    if (!url) {
      toast({
        title: "URL required",
        description: "Enter a URL to scrape.",
        variant: "destructive",
      });
      return;
    }

    setPreviewUrl(url);
    setMode(action === "stored" ? "detail" : action);
    setPreviewLoading(action);
    setPreviewResult(null);

    try {
      const res = await invokeAdapterVisibility({
        action: action === "stored" ? "preview-stored-detail" : action === "detail" ? "preview-detail" : "preview-discovery",
        adapterSlug,
        url,
        config: parsedConfig ?? {},
        ...(previewProvider !== "auto" && action !== "stored" ? { provider: previewProvider } : {}),
      });
      if (!res.success) toast({ title: "Preview failed", description: res.error, variant: "destructive" });
      setPreviewResult(res);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Preview request failed";
      setPreviewResult({ success: false, error: message });
      toast({ title: "Preview failed", description: message, variant: "destructive" });
    } finally {
      setPreviewLoading(null);
    }
  };

  // Cross-tab handoff: if the parent supplies an initialUrl + initialMode,
  // auto-run the preview once when the operator role is available.
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot handoff after auth resolves
  useEffect(() => {
    if (autoRanRef.current) return;
    if (!initialUrl || !canManageJobs) return;
    autoRanRef.current = true;
    runPreview(initialMode ?? "detail", initialUrl);
    // Subsequent edits should not re-trigger the auto-run.
  }, [canManageJobs]);

  const propertyUrls: string[] = useMemo(() => {
    if (!previewResult || !previewResult.success) return [];
    const result = previewResult.result as Record<string, unknown> | undefined;
    const urls = result?.propertyUrls;
    return Array.isArray(urls) ? (urls as string[]) : [];
  }, [previewResult]);

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Copied", description: "URL copied to clipboard." });
    } catch {
      toast({
        title: "Copy failed",
        description: "Clipboard access denied.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Discover &amp; sample</CardTitle>
        <CardDescription>
          Run an inline detail extract on a property URL, or a discovery sweep on a
          listing URL. Click any discovered URL to drill in.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="discover-url">URL</Label>
          <Input
            id="discover-url"
            name="discover-url"
            value={previewUrl}
            onChange={(event) => setPreviewUrl(event.target.value)}
            placeholder="Property or listing URL"
          />
        </div>

        {listingUrls.length > 0 && (
          <div className="space-y-1.5">
            <Label htmlFor="discover-listing-pick" className="text-xs text-muted-foreground">
              Pick a listing URL from the URL builder
            </Label>
            <Select
              value=""
              onValueChange={(value) => {
                if (value) setPreviewUrl(value);
              }}
            >
              <SelectTrigger id="discover-listing-pick">
                <SelectValue placeholder="Select a paginated listing URL…" />
              </SelectTrigger>
              <SelectContent>
                {listingUrls.map((row) => (
                  <SelectItem key={row.page} value={row.url}>
                    Page {row.page} — {row.url}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="discover-provider">Fetch with</Label>
          <Select value={previewProvider} onValueChange={(value) => setPreviewProvider(value as PreviewProvider)}>
            <SelectTrigger id="discover-provider">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PREVIEW_PROVIDERS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Production routing tries providers in the order real jobs use. Picking one provider
            tries only that one, with no fallback: "Direct fetch only" tells you whether a site
            serves its pages without a paid provider. Stored parse uses the latest saved HTML for
            the exact URL and makes no provider request.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="discover-config">Adapter config (JSON object)</Label>
          <Textarea
            id="discover-config"
            name="discover-config"
            value={configJson}
            onChange={(event) => onConfigJsonChange(event.target.value)}
            rows={6}
            className="font-mono text-xs"
          />
          {parsedConfig === null && (
            <p className="text-xs text-destructive">
              Invalid JSON object — fix before running.
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            disabled={previewLoading !== null || !canManageJobs}
            onClick={() => runPreview("detail")}
          >
            {previewLoading === "detail" ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <PlayCircle className="w-4 h-4 mr-2" />
            )}
            Preview detail extract
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={previewLoading !== null || !canManageJobs}
            onClick={() => runPreview("discovery")}
          >
            {previewLoading === "discovery" ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <SearchIcon className="w-4 h-4 mr-2" />
            )}
            Preview discovery (links)
          </Button>
          <Button type="button" variant="outline" disabled={previewLoading !== null || !canManageJobs} onClick={() => runPreview("stored")}>Parse stored page (free)</Button>
        </div>
        {!canManageJobs && <p className="text-xs text-muted-foreground">Operator role required to run previews.</p>}

        {mode === "discovery" && propertyUrls.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">
              Discovered property URLs ({propertyUrls.length})
            </p>
            <ul className="space-y-1.5 text-xs font-mono">
              {propertyUrls.map((url) => (
                <li
                  key={url}
                  className="flex items-center gap-2 border rounded p-2 bg-muted/20"
                >
                  <span className="flex-1 truncate" title={url}>
                    {url}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => runPreview("detail", url)}
                    disabled={previewLoading !== null || !canManageJobs}
                  >
                    Preview
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(url)}
                    aria-label="Copy URL"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:text-foreground"
                    aria-label="Open in new tab"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {previewResult && <div className="space-y-3 rounded-md border p-3 text-sm">
          <div className="flex flex-wrap gap-2 items-center">
            <Badge variant={previewResult.success ? "default" : "destructive"}>{previewResult.success ? "Preview complete" : "Preview failed"}</Badge>
            {previewResult.success && <span>Route: {previewResult.strategy}</span>}
            {previewResult.escalated && <Badge variant="secondary">Escalated to Bright Data</Badge>}
          </div>
          {!previewResult.success && <p className="text-destructive">{previewResult.error}</p>}
          {previewResult.attempts?.length ? <div><p className="font-medium">Provider attempts</p><ul className="space-y-1 text-xs">{previewResult.attempts.map((attempt, index) => <li key={`${String(attempt.provider)}-${index}`} className="border-b py-1">{String(attempt.provider)} · {String(attempt.status)}{attempt.completenessScore != null ? ` · ${attempt.completenessScore}% after parse` : ""}{attempt.errorMessage ? ` · ${String(attempt.errorMessage)}` : ""}</li>)}</ul></div> : null}
          {previewResult.success && mode === "detail" && <div className="space-y-2">
            <p className="font-medium">Served by {String(previewResult.result.provider ?? "stored page")}{previewResult.result.capturedAt ? ` · captured ${String(previewResult.result.capturedAt)}` : ""}</p>
            {Boolean(previewResult.result.parserError) && <p className="text-xs text-destructive">Parser error: {String(previewResult.result.parserError)}</p>}
            <p>Completeness after parser: {String(previewResult.result.completenessScore ?? "—")}%</p>
            <p className="font-medium">Merged object shape and field sources</p>
            <div className="max-h-80 overflow-auto"><table className="w-full text-xs"><thead><tr className="border-b text-left"><th className="py-1">Field</th><th>Value</th><th>Source</th></tr></thead><tbody>{Object.entries(record(previewResult.result.data)).map(([field, value]) => <tr key={field} className="border-b"><td className="font-mono py-1">{field}</td><td className="font-mono break-all">{display(value)}</td><td>{String(record(previewResult.result.fieldSources)[field] ?? "—")}</td></tr>)}</tbody></table></div>
            <details className="text-xs"><summary className="cursor-pointer">Generic and adapter parser inputs</summary><pre className="mt-2 max-h-72 overflow-auto rounded bg-muted p-2">{JSON.stringify({ generic: previewResult.result.genericData, adapter: previewResult.result.parserData }, null, 2)}</pre></details>
            {Boolean(previewResult.result.rawHtml) && <details className="text-xs"><summary className="cursor-pointer">Fetched HTML excerpt</summary><pre className="mt-2 max-h-72 overflow-auto rounded bg-muted p-2">{String(previewResult.result.rawHtml)}</pre></details>}
          </div>}
          {previewResult.success && mode === "discovery" && <p className="text-xs">Served by {String(previewResult.result.provider ?? "—")} · {String(previewResult.result.propertyUrlTotal ?? 0)} URLs found.</p>}
        </div>}
      </CardContent>
    </Card>
  );
}
